import React from "react";
import {
    Typography,
    Box,
    Grid,
    Card,
    CardMedia,
    Button,
    Stack,
    Container,
    Paper,
    Avatar,
} from "@mui/material";

// eslint-disable-next-line no-unused-vars

import CallIcon from "@mui/icons-material/Call";

import NorthWestIcon from "@mui/icons-material/NorthWest";
import HandshakeIcon from "@mui/icons-material/Handshake";
import ShieldIcon from "@mui/icons-material/Shield";
import StarIcon from "@mui/icons-material/Star";
import ScheduleIcon from "@mui/icons-material/Schedule";
import GroupIcon from "@mui/icons-material/Group";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useNavigate } from "react-router-dom";
import { useInView } from "react-intersection-observer";

// Import your images (replace with actual image imports)
import AboutImg1 from "./images/about4.png";
import AboutImg2 from "./images/about7.jpg";
import AboutImg3 from "./images/about8.jpg";
import Header from './components/Header';
import Meta from './components/Meta';

const imageData = [
    {
        id: 1,
        image: AboutImg1,
        height: 290,
        name: "Shihab Pervez",
        title: "Chief Executive Officer (CEO)",
    },
    {
        id: 2,
        image: AboutImg2,
        height: 200,
        name: "Zahir Ahmed",
        title: "Chief Commercial Officer (CCO)",
    },
    {
        id: 3,
        image: AboutImg3,
        height: 320,
        name: "Zahidul Islam",
        title: "Chief Financial Officer (CFO)",
        border: true,
    },
];

const coreValues = [
    {
        icon: <HandshakeIcon fontSize="large" />,
        title: "Integrity",
        description: "Unwavering commitment to ethical business practices and transparent operations in all transactions"
    },
    {
        icon: <ShieldIcon fontSize="large" />,
        title: "Compliance",
        description: "Adherence to all regulatory requirements and government standards across jurisdictions"
    },
    {
        icon: <StarIcon fontSize="large" />,
        title: "Excellence",
        description: "Quality assurance in every transaction, delivery, and client interaction"
    },
    {
        icon: <ScheduleIcon fontSize="large" />,
        title: "Reliability",
        description: "Dependable service and timely fulfillment of all commitments and deliverables"
    },
    {
        icon: <GroupIcon fontSize="large" />,
        title: "Partnership",
        description: "Collaborative relationships with OEMs, suppliers, clients, and government agencies"
    },
    {
        icon: <LightbulbIcon fontSize="large" />,
        title: "Innovation",
        description: "Continuous improvement in procurement processes and service delivery"
    }
];

const milestones = [
    {
        date: "November 13, 2025",
        title: "Incorporation",
        description: "Trillium Sourcing Corporation incorporated under Canada Business Corporations Act"
    },
    {
        date: "November 14, 2025",
        title: "Ontario Registration",
        description: "Ontario Extra-Provincial Registration filed"
    },
    {
        date: "December 10, 2025",
        title: "Strategic Partnership",
        description: "Partnership authorized by Millennium Trade Line"
    },
    {
        date: "January 2026",
        title: "Launch",
        description: "Website launch and operational expansion"
    },
    {
        date: "Q1 2026 onwards",
        title: "Growth",
        description: "Active procurement and supply chain operations"
    }
];

const HoverImage = ({ image, height, name, title, border }) => (
    <Card
        sx={{
            position: "relative",
            boxShadow: 3,
            overflow: "hidden",
            border: border ? "24px solid white" : "none",
        }}
    >
        <Box
            sx={{
                position: "relative",
                cursor: "pointer",
                "&:hover .overlay": { opacity: 1 },
                "&:hover img": {
                    filter: "brightness(60%)",
                    transform: "scale(1.05)",
                },
            }}
        >
            <CardMedia
                component="img"
                image={image}
                height={height}
                sx={{
                    transition: "all 0.4s ease",
                }}
            />

            {/* OVERLAY */}
            <Box
                className="overlay"
                sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(0,0,0,0.45)",
                    color: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    opacity: 0,
                    transition: "opacity 0.4s ease",
                    px: 2,
                }}
            >
                <Typography fontWeight="bold" fontSize={18}>
                    {name}
                </Typography>
                <Typography fontSize={14}>{title}</Typography>
            </Box>
        </Box>
    </Card>
);

function About() {
    const navigate = useNavigate();

    // Animation 1: Company Overview - Fade in from blur
    const { ref: overviewRef, inView: overviewInView } = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    // Animation 2: Mission & Vision - Slide from sides
    const { ref: missionRef, inView: missionInView } = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    // Animation 3: Core Values - Slide up from bottom
    const { ref: valuesRef, inView: valuesInView } = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    // Animation 4: Our Journey - Fade in
    const { ref: journeyRef, inView: journeyInView } = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    return (
        <div>
            <Header />
            <Meta
                title="About Trillium Sourcing Corporation | Toronto-Based Procurement Leader"
                description="Learn about Trillium Sourcing Corporation, our mission, vision, leadership team, and commitment to global procurement excellence."
                keywords="defence procurement, global sourcing, Toronto procurement, supply chain management"
            />

            {/* PAGE HEADER */}
            <Box
                sx={{
                    backgroundColor: "#0F758E",
                    color: "white",
                    py: 8,
                    textAlign: "center",
                }}
            >
                <Container>
                    <Typography variant="h2" fontWeight="bold" gutterBottom>
                        About Trillium Sourcing Corporation
                    </Typography>
                    <Typography variant="h5">
                        A Toronto-Based Global Procurement Partner
                    </Typography>
                </Container>
            </Box>

            {/* COMPANY OVERVIEW SECTION */}
            <Container sx={{ py: 8 }}>
                <Grid container spacing={6} alignItems="center" ref={overviewRef}>
                    {/* LEFT SIDE - IMAGES */}
                    <Grid item xs={12} md={7}>
                        <Stack spacing={2}>
                            <Box sx={{ width: { xs: 300, md: 450 } }}>
                                <HoverImage {...imageData[0]} />
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sx={{ ml: -2, mr: 2 }}>
                                    <HoverImage {...imageData[1]} />
                                </Grid>
                                <Grid item xs={6} sx={{ mt: -16 }}>
                                    <HoverImage {...imageData[2]} />
                                </Grid>
                            </Grid>
                        </Stack>
                    </Grid>

                    {/* RIGHT SIDE - COMPANY OVERVIEW */}
                    <Grid item xs={12} md={5}>
                        <Stack spacing={3}>
                            <Box
                                sx={{
                                    opacity: overviewInView ? 1 : 0,
                                    filter: overviewInView ? 'blur(0)' : 'blur(10px)',
                                    transform: overviewInView ? 'translateY(0)' : 'translateY(20px)',
                                    transition: 'opacity 0.8s ease, filter 0.8s ease, transform 0.8s ease',
                                }}
                            >
                                <Box>
                                    <NorthWestIcon sx={{ color: "#0F758E", mr: 1 }} />
                                    <Typography
                                        variant="overline"
                                        sx={{ fontWeight: "bold", fontSize: 20, color: "#0F758E" }}
                                    >
                                        Company Overview
                                    </Typography>

                                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                                        Toronto-Based Global Procurement Partner
                                    </Typography>

                                    <Typography color="text.secondary" paragraph>
                                        Trillium Sourcing Corporation is a specializ ed procurement and supply chain management firm based in Toronto, Ontario, Canada. Incorporated in November 2025 under the Canada Business Corporations Act, TSC serves as a strategic sourcing partner for government agencies, defence procurers, and OEM network participants across multiple jurisdictions.
                                    </Typography>

                                    <Typography color="text.secondary" paragraph>
                                        With leadership rooted in international trade and procurement expertise, Trillium Sourcing Corporation leverages established networks, regulatory compliance frameworks, and strategic partnerships to bridge the gap between Original Equipment Manufacturers (OEMs), government agencies, and procurement bodies worldwide.
                                    </Typography>
                                </Box>

                                {/* CORPORATION DETAILS */}
                                <Box>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom color="#0F758E">
                                        Corporation Details:
                                    </Typography>
                                    {[
                                        { icon: <BusinessIcon />, text: "Legal Name: Trillium Sourcing Corporation" },
                                        { icon: <CalendarTodayIcon />, text: "Incorporation: Federal (Canada Business Corporations Act)" },
                                        { icon: <BusinessIcon />, text: "Corporation Number: 1747248-0" },
                                        { icon: <CalendarTodayIcon />, text: "Registration Date: November 13, 2025" },
                                        { icon: <LocationOnIcon />, text: "Headquarters: Unit C, 2155 Lawrence Avenue East, Toronto, ON M1R 5G9, Canada" },
                                        { icon: <CallIcon />, text: "Phone: +1 647 545 7225" },
                                        { icon: <EmailIcon />, text: "Email: info@trilliumsourcing.com" },
                                    ].map((item, index) => (
                                        <Typography
                                            key={index}
                                            sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
                                        >
                                            <Box sx={{ color: "#0F758E" }}>{item.icon}</Box>
                                            {item.text}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>

            {/* MISSION & VISION SECTION */}
            <Box sx={{ backgroundColor: "#f5f5f5", py: 8 }} ref={missionRef}>
                <Container>
                    <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
                        Mission & Vision
                    </Typography>

                    <Grid container spacing={4} sx={{ mt: 2 }}>
                        {/* ====== Mission Card ====== */}
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={3}
                                sx={{
                                    p: 4,
                                    height: "100%",
                                    backgroundColor: "#0F758E",
                                    color: "white",
                                    borderRadius: 2,
                                    transition: "all 0.8s ease",
                                    transform: missionInView ? "translateX(0)" : "translateX(-100px)",
                                    opacity: missionInView ? 1 : 0,
                                    "&:hover": {
                                        transform: "scale(1.03)",
                                        backgroundColor: "white",
                                        color: "#0F758E",
                                    },
                                }}
                            >
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    Mission Statement
                                </Typography>
                                <Typography>
                                    To provide reliable, compliant, and efficient procurement solutions for defence contracts, equipment supply, and global product sourcing while maintaining the highest standards of quality assurance, regulatory compliance, and professional integrity.
                                </Typography>
                            </Paper>
                        </Grid>

                        {/* ====== Vision Card ====== */}
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={3}
                                sx={{
                                    p: 4,
                                    height: "100%",
                                    backgroundColor: "white",
                                    border: "2px solid #0F758E",
                                    borderRadius: 2,
                                    transition: "all 0.8s ease",
                                    transform: missionInView ? "translateX(0)" : "translateX(100px)",
                                    opacity: missionInView ? 1 : 0,
                                    "&:hover": {
                                        transform: "scale(1.03)",
                                        backgroundColor: "#0F758E",
                                        color: "white",
                                    },
                                    "&:hover h5": {
                                        color: "white",
                                    },
                                }}
                            >
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    Vision Statement
                                </Typography>

                                <Typography>
                                    To become a recognized leader in cross-border procurement and supply chain management, serving government and commercial clients with transparency, excellence, and strategic capability.
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>


            {/* CORE VALUES SECTION */}
            <Container sx={{ py: 8 }} ref={valuesRef}>
                <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
                    Our Core Values
                </Typography>
                <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
                    The principles that guide our operations and client relationships
                </Typography>

                <Grid container spacing={4}>
                    {coreValues.map((value, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card
                                sx={{
                                    p: 3,
                                    height: "100%",
                                    textAlign: "center",
                                    transition: "transform 0.8s ease, opacity 0.8s ease",
                                    transform: valuesInView ? "translateY(0)" : "translateY(50px)",
                                    opacity: valuesInView ? 1 : 0,
                                    transitionDelay: `${index * 0.1}s`,
                                    "&:hover": {
                                        transform: "translateY(-8px)",
                                        boxShadow: 6,
                                    },
                                }}
                            >
                                <Box sx={{ color: "#0F758E", mb: 2 }}>
                                    {value.icon}
                                </Box>
                                <Typography variant="h6" fontWeight="bold" gutterBottom color="#0F758E">
                                    {value.title}
                                </Typography>
                                <Typography color="text.secondary">
                                    {value.description}
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* LEADERSHIP TEAM SECTION */}
            <Box sx={{ backgroundColor: "#f5f5f5", py: 8 }}>
                <Container>
                    <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
                        Leadership Team
                    </Typography>
                    <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
                        Meet our executive leadership
                    </Typography>

                    <Grid container spacing={4} justifyContent="center">
                        {/* CEO CARD */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ p: 4 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={4}>
                                        <Avatar
                                            src={AboutImg1}
                                            sx={{ width: 120, height: 120, mb: 2 }}
                                        />
                                        <Typography fontWeight="bold">Quazi Farhad Ahmed</Typography>
                                        <Typography color="#0F758E">Chief Executive Officer (CEO)</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={8}>
                                        <Typography paragraph>
                                            Quazi Farhad Ahmed brings extensive international trade and procurement expertise to his role as CEO of Trillium Sourcing Corporation. With a focus on strategic direction, contract management, and government relations, Quazi leads TSC's expansion into global defence and procurement markets.
                                        </Typography>
                                        <Typography fontWeight="bold" gutterBottom>
                                            Key Responsibilities:
                                        </Typography>
                                        <ul>
                                            <li>Strategic planning and corporate direction</li>
                                            <li>Government agency relationships and contract oversight</li>
                                            <li>International partnership management</li>
                                            <li>Risk management and compliance supervision</li>
                                        </ul>
                                        <Box sx={{ mt: 2 }}>
                                            <Typography>
                                                <LocationOnIcon fontSize="small" sx={{ mr: 1, color: "#0F758E" }} />
                                                Unit C, 2155 Lawrence Avenue East, Toronto, ON M1R 5G9
                                            </Typography>
                                            <Typography>
                                                <CallIcon fontSize="small" sx={{ mr: 1, color: "#0F758E" }} />
                                                +1 647 545 7225
                                            </Typography>
                                            <Typography>
                                                <EmailIcon fontSize="small" sx={{ mr: 1, color: "#0F758E" }} />
                                                quazi@trilliumsourcing.com
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>

                        {/* CCO CARD */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ p: 4 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={4}>
                                        <Avatar
                                            src={AboutImg2}
                                            sx={{ width: 120, height: 120, mb: 2 }}
                                        />
                                        <Typography fontWeight="bold">Zahir Ahmed</Typography>
                                        <Typography color="#0F758E">Chief Commercial Officer (CCO)</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={8}>
                                        <Typography paragraph>
                                            Zahir Ahmed serves as Chief Commercial Officer, driving sales, business development, and commercial negotiations for Trillium Sourcing Corporation. His expertise in supplier relationships and market development strengthens TSC's position in global procurement.
                                        </Typography>
                                        <Typography fontWeight="bold" gutterBottom>
                                            Key Responsibilities:
                                        </Typography>
                                        <ul>
                                            <li>Sales and business development initiatives</li>
                                            <li>Commercial contract negotiation and strategy</li>
                                            <li>OEM and supplier relationship management</li>
                                            <li>Market intelligence and opportunity identification</li>
                                            <li>Pricing and margin optimization</li>
                                        </ul>
                                        <Box sx={{ mt: 2 }}>
                                            <Typography>
                                                <LocationOnIcon fontSize="small" sx={{ mr: 1, color: "#0F758E" }} />
                                                Unit C, 2155 Lawrence Avenue East, Toronto, ON M1R 5G9
                                            </Typography>
                                            <Typography>
                                                <CallIcon fontSize="small" sx={{ mr: 1, color: "#0F758E" }} />
                                                +1 647 917 6248
                                            </Typography>
                                            <Typography>
                                                <EmailIcon fontSize="small" sx={{ mr: 1, color: "#0F758E" }} />
                                                zahir@trilliumsourcing.com
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* COMPANY HISTORY & MILESTONES */}
            <Container sx={{ py: 8 }} ref={journeyRef}>
                <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
                    Our Journey
                </Typography>

                <Box sx={{ position: "relative", mt: 6 }}>
                    {/* TIMELINE LINE */}
                    <Box
                        sx={{
                            position: "absolute",
                            left: { xs: "50%", md: "50%" },
                            transform: "translateX(-50%)",
                            width: "2px",
                            height: "100%",
                            backgroundColor: "#0F758E",
                        }}
                    />

                    {milestones.map((milestone, index) => (
                        <Grid
                            container
                            key={index}
                            sx={{
                                position: "relative",
                                mb: 6,
                                flexDirection: index % 2 === 0 ? "row" : "row-reverse",
                            }}
                        >
                            <Grid item xs={12} md={5}>
                                <Card
                                    sx={{
                                        p: 3,
                                        mx: 2,
                                        backgroundColor: index % 2 === 0 ? "#0F758E" : "white",
                                        color: index % 2 === 0 ? "white" : "inherit",
                                        border: index % 2 !== 0 ? "2px solid #0F758E" : "none",
                                        transition: "all 0.8s ease",
                                        opacity: journeyInView ? 1 : 0,
                                        transform: journeyInView ? "scale(1)" : "scale(0.9)",
                                        transitionDelay: `${index * 0.2}s`,
                                    }}
                                >
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        {milestone.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        {milestone.date}
                                    </Typography>
                                    <Typography>
                                        {milestone.description}
                                    </Typography>
                                </Card>
                            </Grid>

                            {/* TIMELINE DOT */}
                            <Grid item xs={12} md={2} sx={{ position: "relative" }}>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        width: 20,
                                        height: 20,
                                        borderRadius: "50%",
                                        backgroundColor: "#0F758E",
                                        border: "4px solid white",
                                        zIndex: 1,
                                    }}
                                />
                            </Grid>

                            {/* SPACER */}
                            <Grid item xs={12} md={5} />
                        </Grid>
                    ))}
                </Box>
            </Container>

            {/* CTA SECTION */}
            <Box
                sx={{
                    backgroundColor: "#0F758E",
                    color: "white",
                    py: 6,
                    textAlign: "center",
                }}
            >
                <Container>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Ready to Partner with Trillium Sourcing?
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                        Contact us today to discuss your procurement needs
                    </Typography>
                    <Stack direction="row" spacing={3} justifyContent="center">
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: "white",
                                color: "#0F758E",
                                fontWeight: "bold",
                                "&:hover": {
                                    backgroundColor: "#f5f5f5",
                                },
                            }}
                            onClick={() => navigate("/contact")}
                        >
                            Contact Us
                        </Button>
                        <Button
                            variant="outlined"
                            sx={{
                                borderColor: "white",
                                color: "white",
                                fontWeight: "bold",
                                "&:hover": {
                                    borderColor: "#f5f5f5",
                                    backgroundColor: "rgba(255,255,255,0.1)",
                                },
                            }}
                            onClick={() => navigate("/services")}
                        >
                            Our Services
                        </Button>
                    </Stack>
                </Container>
            </Box>
        </div>
    );
}

export default About;